/**
 * SUNTRONIX ONE — Executive Minimal sign-in.
 *
 * ╔════════════════════════════════════════════════════════════════════╗
 * ║  🔒 BRAND LOGO LOCK — DO NOT MODIFY (user directive, permanent)   ║
 * ║  The logo on this page is served from                              ║
 * ║      /suntronix-logo-official.webp?v=color1                        ║
 * ║  and MUST NEVER be swapped, resized inconsistently, replaced with  ║
 * ║  text, wrapped with additional wordmarks, tinted, or otherwise     ║
 * ║  altered by any future agent or refactor.  This is the official   ║
 * ║  brand asset extracted from the customer-provided PDF; any change ║
 * ║  is a brand violation.  See /app/memory/LOGO_LOCK.md.             ║
 * ╚════════════════════════════════════════════════════════════════════╝
 *
 * Design language:
 *   • Palette: pure white, warm white (#FAFAF7), soft grey borders (#E7E7E4),
 *     charcoal text (#1B1D1F), deep navy accent (#0A1A2F).
 *   • No gradients, no glow, no illustrations. Sophistication comes from
 *     typography, alignment, whitespace and micro-interactions only.
 *   • Two-column at ≥lg (60/40) that reads as a single calm page — no dark canvas.
 *   • Authentication logic, tenant discovery, MFA/SSO/passkey flows, and every
 *     test-id are preserved.
 */
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import {
  ArrowRight, Eye, EyeOff, Loader2, HelpCircle, ShieldCheck,
  ChevronLeft, Activity,
} from "lucide-react";
import api from "@/lib/api";
import { defaultLanding } from "@/constants/roleNav";
import LanguageRegionPicker from "@/components/app/LanguageRegionPicker";
import LoginStoryPanel from "@/components/app/LoginStoryPanel";
import LifecycleWheel from "@/components/app/LifecycleWheel";
import DiscoverSuntronix from "@/components/app/DiscoverSuntronix";

const LIFECYCLE = ["Procure", "Deploy", "Manage", "Support", "Recover", "Reuse", "Recycle"];
const CONNECTIONS = ["Technology estate", "Every asset", "Every stage", "One platform"];
const MICRO_STATEMENTS = [
  "Every asset. One history.",
  "Every order. One trail.",
  "Every location. One view.",
  "Every lifecycle. One platform.",
];
const PROOF_POINTS = [
  { title: "One Golden Asset Record", body: "Every serial, every stage, one authoritative history." },
  { title: "Complete chain of custody", body: "From procurement through responsible end-of-life." },
  { title: "Global visibility. Local execution.", body: "One platform across every country you operate." },
];
const REMEMBERED_EMAIL_KEY = "st.auth.rememberedEmail";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const postLoginTarget = (role) => {
    if (nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")) return nextParam;
    return defaultLanding(role);
  };

  const [step, setStep] = useState("identify");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tenantHint, setTenantHint] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [rememberedEmail, setRememberedEmail] = useState(null);
  const [showDiscover, setShowDiscover] = useState(false);
  const [beaconCount, setBeaconCount] = useState(null);
  const [beaconDisplay, setBeaconDisplay] = useState(0);
  const beaconDisplayRef = useRef(0);
  const [welcomeFlash, setWelcomeFlash] = useState(null);
  const [storyIndex, setStoryIndex] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return window.localStorage.getItem("st.welcomeSound") === "1"; }
    catch { return false; }
  });
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Aurora lifecycle progression + rotating micro-statements.
  const [activeStage, setActiveStage] = useState(0);
  const [microIndex, setMicroIndex] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t1 = setInterval(() => setActiveStage((i) => (i + 1) % LIFECYCLE.length), 1800);
    const t2 = setInterval(() => setMicroIndex((i) => (i + 1) % MICRO_STATEMENTS.length), 3500);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  // Login vitality beacon — refreshes every 60s so admins feel the platform is alive.
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/auth/login-beacon`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (alive && d) setBeaconCount(d.tenants_active_last_hour); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Count-up animation for the beacon so the pill visibly comes to life.
  useEffect(() => {
    if (beaconCount === null || beaconCount === 0) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { beaconDisplayRef.current = beaconCount; setBeaconDisplay(beaconCount); return; }
    const start = performance.now();
    const from = beaconDisplayRef.current;
    const to = beaconCount;
    const dur = 600;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(from + (to - from) * eased);
      beaconDisplayRef.current = next;
      setBeaconDisplay(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [beaconCount]);

  // Tenant welcome flash — briefly show a big card on recognition + optional chime.
  useEffect(() => {
    if (step === "authenticate" && tenantHint?.tenant_name && !welcomeFlash) {
      setWelcomeFlash(tenantHint);
      if (soundEnabled) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) {
            const ctx = new AC();
            const now = ctx.currentTime;
            [
              { f: 660, t: now,        d: 0.18 },
              { f: 880, t: now + 0.09, d: 0.22 },
            ].forEach(({ f, t: tt, d }) => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.type = "sine"; osc.frequency.value = f;
              gain.gain.setValueAtTime(0, tt);
              gain.gain.linearRampToValueAtTime(0.08, tt + 0.02);
              gain.gain.exponentialRampToValueAtTime(0.0001, tt + d);
              osc.connect(gain); gain.connect(ctx.destination);
              osc.start(tt); osc.stop(tt + d + 0.02);
            });
            setTimeout(() => ctx.close(), 700);
          }
        } catch { /* audio blocked — silent fallback */ }
      }
      const t = setTimeout(() => setWelcomeFlash(null), 900);
      return () => clearTimeout(t);
    }
  }, [step, tenantHint?.tenant_name]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      const remembered = window.localStorage.getItem(REMEMBERED_EMAIL_KEY);
      if (remembered) { setRememberedEmail(remembered); setEmail(remembered); }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (step === "credentials") passwordInputRef.current?.focus();
    else emailInputRef.current?.focus();
  }, [step]);

  if (user) return <Navigate to={defaultLanding(user.role)} replace />;

  const identify = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPlausibleEmail(email)) { setError("Please enter a valid work email."); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/auth/tenant-hint", { params: { email: email.trim().toLowerCase() } });
      if (data?.tenant_name) setTenantHint(data);
    } catch { /* silent — never leak enumeration */ }
    setLoading(false);
    setStep("credentials");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { ok } = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (ok) {
      try { window.localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim().toLowerCase()); } catch { /* ignore */ }
      toast.success("Welcome back");
      navigate(nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/");
    } else {
      setError("We couldn't sign you in with those details. Check your information or use another authorised sign-in method.");
    }
  };

  const useAnotherAccount = () => {
    try { window.localStorage.removeItem(REMEMBERED_EMAIL_KEY); } catch { /* ignore */ }
    setRememberedEmail(null);
    setEmail(""); setPassword("");
    setStep("identify"); setTenantHint(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#050B1B] text-white relative overflow-hidden" data-testid="login-page">
      {/* Full-viewport Electric Aurora — deep navy canvas with saturated glows */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[#050B1B]" />
        <div className="aurora-blob absolute -top-56 -left-40 h-[900px] w-[900px] rounded-full blur-[160px] opacity-[0.70] bg-[radial-gradient(circle_at_center,#2962FF_0%,rgba(41,98,255,0)_70%)]"
                style={{ animation: "aurora-drift-1 26s ease-in-out infinite" }} />
        <div className="aurora-blob absolute top-1/4 left-1/2 -translate-x-1/2 h-[900px] w-[1100px] rounded-full blur-[180px] opacity-[0.55] bg-[radial-gradient(circle_at_center,#00C8FF_0%,rgba(0,200,255,0)_65%)]"
                style={{ animation: "aurora-drift-2 32s ease-in-out infinite" }} />
        <div className="aurora-blob absolute -bottom-56 -right-40 h-[900px] w-[900px] rounded-full blur-[160px] opacity-[0.55] bg-[radial-gradient(circle_at_center,#7657FF_0%,rgba(118,87,255,0)_70%)]"
                style={{ animation: "aurora-drift-3 28s ease-in-out infinite" }} />
        <div className="aurora-blob absolute bottom-10 left-10 h-[420px] w-[420px] rounded-full blur-[130px] opacity-[0.35] bg-[radial-gradient(circle_at_center,#18D5A4_0%,rgba(24,213,164,0)_70%)]"
                style={{ animation: "aurora-drift-4 30s ease-in-out infinite" }} />
        {/* Fine coordinate grid */}
        <svg className="absolute inset-0 h-full w-full opacity-[0.045]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-aurora" width="72" height="72" patternUnits="userSpaceOnUse">
              <path d="M 72 0 L 0 0 0 72" fill="none" stroke="#C6D2EA" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-aurora)" />
        </svg>
        {/* Vignette for depth */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,11,27,0.55)_100%)]" />
      </div>

      {/* ============================================================ HEADER (glassy) */}
      <header className="relative z-10 border-b border-white/10 backdrop-blur-sm bg-white/[0.02]">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/suntronix-logo-official.webp?v=color1" alt="Suntronix"
                  className="h-6 w-auto block bg-white rounded-md px-2 py-1 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.55)]"
                  data-testid="header-brand-logo" />
            <span className="hidden sm:inline text-[11px] tracking-[0.20em] uppercase text-[#C6D2EA]/70 font-medium">
              Global Technology Lifecycle Platform
            </span>
          </div>
          <nav aria-label="Utilities" className="flex items-center gap-5 text-[12px] text-[#C6D2EA]/80">
            <button type="button" onClick={() => setShowHelp(true)} className="hover:text-white transition-colors">Help</button>
            <button type="button"
                    onClick={() => {
                      const next = !soundEnabled;
                      setSoundEnabled(next);
                      try { window.localStorage.setItem("st.welcomeSound", next ? "1" : "0"); } catch { /* ignore */ }
                    }}
                    data-testid="login-sound-toggle"
                    aria-label={soundEnabled ? "Disable welcome chime" : "Enable welcome chime"}
                    className="hover:text-white transition-colors inline-flex items-center gap-1.5">
              <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${soundEnabled ? "bg-[#00C8FF] shadow-[0_0_10px_#00C8FF]" : "bg-white/25"}`} />
              Sound {soundEnabled ? "on" : "off"}
            </button>
            <a href="/help/status" className="hover:text-white transition-colors inline-flex items-center gap-1.5">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_#18D5A4] inline-block" />
              All systems operational
            </a>
            <LanguageRegionPicker />
          </nav>
        </div>
      </header>

      {/* ============================================================ MAIN */}
      <main className="flex-1 relative">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10 grid lg:grid-cols-[minmax(0,60fr)_minmax(0,40fr)] gap-x-14 min-h-[calc(100dvh-64px)]">
          {/* ---------------- Brand column (Electric Aurora + brand story) ---------------- */}
          <section aria-label="SUNTRONIX ONE" className="hidden lg:flex flex-col justify-between py-14 relative z-10">
            <div>
              {/* Official Suntronix logo — bold clean tile on aurora */}
              <div className="inline-flex items-center gap-4">
                <div className="rounded-xl bg-white px-5 py-3 shadow-[0_20px_50px_-16px_rgba(0,0,0,0.5)] ring-1 ring-white/40">
                  <img
                    src="/suntronix-logo-official.webp?v=color1"
                    alt="Suntronix — official corporate logo"
                    className="h-12 w-auto block"
                    data-testid="login-suntronix-logo"
                  />
                </div>
                <div className="pl-1">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#00C8FF] font-semibold">Since 1999</div>
                  <div className="text-[13px] text-white/85 mt-0.5 tracking-tight">Pioneering enterprise technology</div>
                </div>
              </div>

              <div className="mt-10">
                <LoginStoryPanel index={storyIndex} onIndexChange={setStoryIndex} />
              </div>

              <div className="mt-10 hidden xl:block">
                <LifecycleWheel
                  activeStage={storyIndex}
                  onStageClick={(idx) => setStoryIndex(idx % 7)} />
              </div>

              {/* Vitality beacon — never exposes tenant identities */}
              {beaconCount !== null && beaconCount > 0 && (
                <div className="mt-8 inline-flex items-center gap-2 text-[11px] text-[#C6D2EA]/85 border border-white/10 rounded-full px-3 py-1.5 bg-white/[0.02]"
                        data-testid="login-vitality-beacon">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#00C8FF] shadow-[0_0_12px_#00C8FF] animate-pulse" />
                  <span className="tabular-nums font-semibold text-white">{beaconDisplay}</span>
                  {beaconCount === 1 ? "tenant" : "tenants"} signed in in the last hour
                </div>
              )}
            </div>

            {/* Enterprise proof — 3 stacked lines with cyan pulse */}
            <div className="mt-12 grid grid-cols-3 gap-x-6 gap-y-3 max-w-[640px]">
              {[
                "Established 1999",
                "Enterprise technology ops",
                "Pan-India delivery & service",
                "Apple Business workflows",
                "Multi-brand technology",
                "Forward & reverse logistics",
                "Warehousing & lifecycle ops",
                "Secure asset recovery",
                "Customer-specific controls",
              ].map((p) => (
                <div key={p} className="flex items-start gap-2 text-[11px] text-[#C6D2EA]/85 leading-snug">
                  <span aria-hidden className="mt-1 h-1.5 w-1.5 rounded-full bg-[#00C8FF] shadow-[0_0_10px_#00C8FF] flex-shrink-0" />
                  <span>{p}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button type="button" onClick={() => setShowDiscover(true)}
                        data-testid="login-discover-btn"
                        className="text-[12px] text-white hover:text-[#00C8FF] inline-flex items-center gap-2 border border-white/25 hover:border-[#00C8FF] hover:bg-[#00C8FF]/10 rounded-full px-4 py-2 transition-all font-medium tracking-tight">
                Discover Suntronix
                <span aria-hidden className="text-[#00C8FF]">→</span>
              </button>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#C6D2EA]/50">
                Chennai · Global Operations
              </div>
            </div>
          </section>

          {/* ---------------- Authentication column (floating white card on aurora) ---------------- */}
          <section aria-label="Sign in" className="py-8 lg:py-16 flex items-center justify-center relative z-10">
            <div className="w-full max-w-[440px] bg-white rounded-3xl shadow-[0_40px_120px_-24px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.35)] p-8 lg:p-10 relative">
              {/* Subtle inner cyan glow ring */}
              <div aria-hidden className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-b from-white/70 via-transparent to-transparent" />
              {/* Mobile heading — compact Aurora hero with logo tile + lifecycle pulse */}
              <div className="lg:hidden mb-8 -mx-4 sm:-mx-0 rounded-2xl relative overflow-hidden bg-[#081225] p-6 border border-white/10"
                      data-testid="login-mobile-hero">
                <div aria-hidden className="pointer-events-none absolute inset-0">
                  <div className="absolute -top-16 -left-16 h-48 w-48 rounded-full blur-3xl opacity-60 bg-[radial-gradient(circle,#2962FF_0%,rgba(41,98,255,0)_70%)]" />
                  <div className="absolute -bottom-20 -right-10 h-52 w-52 rounded-full blur-3xl opacity-50 bg-[radial-gradient(circle,#00C8FF_0%,rgba(0,200,255,0)_70%)]" />
                </div>
                <div className="relative">
                  <div className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-[0_6px_18px_-8px_rgba(6,12,32,0.55)]">
                    <img src="/suntronix-logo-official.webp?v=color1" alt="Suntronix"
                          className="h-6 w-auto block"
                          data-testid="login-mobile-logo" />
                  </div>
                  <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-[#C6D2EA]/80">
                    Pioneering enterprise technology since 1999
                  </div>
                  <h1 className="mt-2 font-heading text-[24px] leading-[1.15] tracking-[-0.02em] text-white">
                    Your technology estate.<br />
                    <span className="bg-gradient-to-r from-[#00C8FF] to-[#2962FF] bg-clip-text text-transparent">Managed as one.</span>
                  </h1>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {LIFECYCLE.map((stage, idx) => {
                      const colors = ["#2962FF","#00C8FF","#18D5A4","#FFB84D","#7657FF","#18D5A4","#0F9F7A"];
                      const active = idx === activeStage;
                      return (
                        <span key={stage}
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] tracking-[0.12em] uppercase transition-all duration-500 ${
                                  active
                                    ? "border-white/25 bg-white/10 text-white"
                                    : "border-white/10 text-[#C6D2EA]/60"}`}>
                          <span aria-hidden className="h-1.5 w-1.5 rounded-full transition-all duration-500"
                                   style={{ background: colors[idx],
                                                boxShadow: active ? `0 0 8px ${colors[idx]}` : "none" }} />
                          {stage}
                        </span>
                      );
                    })}
                  </div>
                  <button type="button" onClick={() => setShowDiscover(true)}
                            data-testid="login-mobile-discover"
                            className="mt-4 text-[11px] text-white/85 hover:text-white inline-flex items-center gap-2 border border-white/20 rounded-full px-3 py-1 transition-colors">
                    Discover Suntronix
                    <span aria-hidden className="text-[#00C8FF]">→</span>
                  </button>
                </div>
              </div>

              {step === "identify" ? (
                <form onSubmit={identify} noValidate className="space-y-7">
                  <header>
                    <h2 className="font-heading text-[26px] leading-tight tracking-[-0.015em] text-[#0A1A2F]">Welcome to ONE</h2>
                    <p className="mt-2 text-[14px] leading-relaxed text-[#5A5C60]">
                      Access your organisation&apos;s technology workspace.
                    </p>
                  </header>

                  {rememberedEmail && email === rememberedEmail && (
                    <div className="rounded-md border border-[#E7E7E4] bg-[#FAFAF7] px-4 py-3 flex items-center gap-3" data-testid="login-remembered">
                      <div className="h-8 w-8 rounded-full bg-[#0A1A2F] text-white grid place-items-center text-[11px] font-medium tracking-tight">
                        {(rememberedEmail[0] || "u").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8B8D]">Continue as</div>
                        <div className="text-[13px] text-[#1B1D1F] font-medium truncate">{rememberedEmail}</div>
                      </div>
                      <button type="button" onClick={useAnotherAccount} className="text-[11px] text-[#5A5C60] hover:text-[#0A1A2F] underline-offset-4 hover:underline" data-testid="login-use-another">
                        Use another
                      </button>
                    </div>
                  )}

                  <FormField label="Work email" htmlFor="email">
                    <input
                      ref={emailInputRef}
                      id="email"
                      data-testid={AUTH.emailInput}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                      placeholder="name@company.com"
                      required
                      className="input-auth"
                    />
                  </FormField>

                  {error && <ErrorAlert>{error}</ErrorAlert>}

                  <PrimaryButton type="submit" loading={loading} data-testid="login-continue">
                    Continue <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                  </PrimaryButton>

                  <button
                    type="button"
                    onClick={() => setShowHelp(true)}
                    className="text-[12px] text-[#5A5C60] hover:text-[#0A1A2F] inline-flex items-center gap-1.5 transition-colors"
                    data-testid="login-help-open">
                    <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Need help signing in?
                  </button>
                </form>
              ) : (
                <form onSubmit={submit} noValidate className="space-y-7">
                  <button
                    type="button"
                    onClick={() => { setStep("identify"); setError(""); setTenantHint(null); }}
                    className="text-[12px] text-[#5A5C60] hover:text-[#0A1A2F] inline-flex items-center gap-1 transition-colors"
                    data-testid="login-back">
                    <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Change email
                  </button>

                  <header>
                    {/* Customer recognition — animated logo + workspace label */}
                    {tenantHint?.tenant_name && (
                      <div className="mb-5 relative rounded-xl border border-[#E1E7F0] bg-gradient-to-br from-[#EFF5FF] via-[#F0FBFF] to-white p-4 overflow-hidden"
                              data-testid="login-tenant-recognition"
                              style={{ animation: "tenant-in 520ms cubic-bezier(0.22, 1, 0.36, 1)" }}>
                        <span aria-hidden className="absolute -top-8 -right-8 h-24 w-24 rounded-full blur-2xl opacity-40 bg-[radial-gradient(circle,#00C8FF_0%,rgba(0,200,255,0)_70%)]" />
                        <span aria-hidden className="absolute -bottom-10 -left-6 h-24 w-24 rounded-full blur-2xl opacity-30 bg-[radial-gradient(circle,#2962FF_0%,rgba(41,98,255,0)_70%)]" />
                        <div className="relative flex items-center gap-3">
                          <div className="h-11 w-11 rounded-lg bg-white border border-[#E1E7F0] grid place-items-center overflow-hidden shadow-sm">
                            {tenantHint.tenant_logo_url ? (
                              <img src={tenantHint.tenant_logo_url}
                                    alt={`${tenantHint.tenant_name} logo`}
                                    className="max-h-9 max-w-9 object-contain" />
                            ) : (
                              <span className="text-[15px] font-heading text-[#0D1B3D] tracking-tight">
                                {(tenantHint.tenant_name[0] || "•").toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.16em] uppercase text-[#4A5470]">Welcome to</div>
                            <div className="text-[15px] font-semibold text-[#0D1B3D] truncate leading-tight">
                              {tenantHint.tenant_name}
                            </div>
                            <div className="text-[11px] text-[#00668A] mt-0.5 flex items-center gap-1.5">
                              Technology Operations Workspace
                              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[#18D5A4] shadow-[0_0_8px_#18D5A4]" />
                              <span className="text-[#169C70]">Tenant securely identified</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <h2 className="font-heading text-[24px] leading-tight tracking-[-0.015em] text-[#0A1A2F]">
                      {tenantHint?.tenant_name ? "Continue with your organisation" : "Sign in"}
                    </h2>
                    <p className="mt-2 text-[14px] leading-relaxed text-[#5A5C60]">
                      Signing in as <span className="text-[#0A1A2F] font-medium">{email}</span>.
                    </p>
                  </header>

                  {tenantHint?.sso_enabled && tenantHint?.sso_url && (
                    <a
                      href={tenantHint.sso_url}
                      className="w-full h-11 rounded-md border border-[#E7E7E4] bg-white text-[#0A1A2F] text-[14px] font-medium hover:bg-[#FAFAF7] focus:outline-none focus:ring-2 focus:ring-[#0A1A2F]/10 transition-colors flex items-center justify-center gap-2"
                      data-testid="login-sso">
                      <ShieldCheck className="h-4 w-4 text-[#5A5C60]" strokeWidth={1.6} /> Continue with Enterprise SSO
                    </a>
                  )}

                  {tenantHint?.passkey_enabled && (
                    <button
                      type="button"
                      onClick={() => toast.info("Passkey sign-in will be available shortly for this tenant.")}
                      className="w-full h-11 rounded-md border-2 border-[#2962FF] bg-[#EFF5FF] text-[#2962FF] text-[14px] font-semibold tracking-[-0.005em] hover:bg-[#DEE9FF] focus:outline-none focus:ring-4 focus:ring-[#2962FF]/20 transition-colors flex items-center justify-center gap-2 shadow-[0_6px_18px_-10px_rgba(41,98,255,0.55)]"
                      data-testid="login-passkey">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 12c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4Z"/>
                        <path d="M10 12H3l2 2-2 2 2 2 2-2 2 2v-4h1"/>
                      </svg>
                      Continue with a passkey
                    </button>
                  )}

                  {tenantHint?.passkey_enabled && (
                    <div className="text-[11px] text-[#8A8B8D] text-center -mt-3">or continue with your password</div>
                  )}

                  <FormField
                    label="Password"
                    htmlFor="password"
                    trailing={<Link to="/forgot-password" className="text-[12px] text-[#5A5C60] hover:text-[#0A1A2F]" tabIndex={-1} data-testid="login-forgot">Forgot password?</Link>}>
                    <div className="relative">
                      <input
                        ref={passwordInputRef}
                        id="password"
                        data-testid={AUTH.passwordInput}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(""); }}
                        placeholder="Enter password"
                        required
                        className="input-auth pr-10"
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 h-full px-3 text-[#8A8B8D] hover:text-[#0A1A2F] transition-colors"
                        tabIndex={-1}
                        data-testid="login-show-password">
                        {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.6} /> : <Eye className="h-4 w-4" strokeWidth={1.6} />}
                      </button>
                    </div>
                  </FormField>

                  {error && <ErrorAlert>{error}</ErrorAlert>}

                  <PrimaryButton type="submit" loading={loading} data-testid={AUTH.submitBtn}>
                    Sign in
                  </PrimaryButton>

                  <div className="flex items-center justify-end text-[12px] text-[#5A5C60]">
                    <button
                      type="button"
                      onClick={() => setShowHelp(true)}
                      className="hover:text-[#0A1A2F] inline-flex items-center gap-1.5 transition-colors">
                      <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} /> Help
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* ============================================================ TRUST BAR / FOOTER */}
      <footer className="border-t border-[#E7E7E4]">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-y-2 text-[11px] text-[#8A8B8D]">
          <div>© {new Date().getFullYear()} Suntronix. All rights reserved.</div>
          <nav aria-label="Trust" className="flex flex-wrap gap-x-6 gap-y-1">
            <a href="/help/trust" className="hover:text-[#0A1A2F] transition-colors">Security &amp; Privacy</a>
            <a href="/help/accessibility" className="hover:text-[#0A1A2F] transition-colors">Accessibility</a>
            <a href="/help/status" className="hover:text-[#0A1A2F] transition-colors inline-flex items-center gap-1"><Activity className="h-3 w-3" strokeWidth={1.5} /> System status</a>
            <a href="/help" className="hover:text-[#0A1A2F] transition-colors">Help Centre</a>
          </nav>
        </div>
      </footer>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 backdrop-blur-sm">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between text-[11px] text-[#C6D2EA]/60">
          <div>© {new Date().getFullYear()} Suntronix. All rights reserved.</div>
          <nav className="flex items-center gap-5">
            <a href="/help/trust" className="hover:text-white transition-colors">Privacy</a>
            <a href="/help/trust" className="hover:text-white transition-colors">Security</a>
            <a href="/help/trust" className="hover:text-white transition-colors">Terms</a>
            <button type="button" onClick={() => setShowHelp(true)} className="hover:text-white transition-colors">Support</button>
          </nav>
        </div>
      </footer>

      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
      {showDiscover && <DiscoverSuntronix onClose={() => setShowDiscover(false)} />}
      {/* Tenant welcome flash — big card fades in on recognition, collapses to compact strip */}
      {welcomeFlash && (
        <div className="fixed inset-0 z-40 flex items-center justify-center pointer-events-none"
                data-testid="tenant-welcome-flash"
                style={{ animation: "welcome-in 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards" }}>
          <div className="rounded-3xl bg-white/95 backdrop-blur-md px-10 py-8 shadow-[0_60px_140px_-24px_rgba(0,0,0,0.7),0_0_0_1px_rgba(0,200,255,0.35)] max-w-md text-center">
            <div className="h-20 w-20 rounded-2xl bg-white border border-[#E1E7F0] grid place-items-center mx-auto overflow-hidden shadow-sm">
              {welcomeFlash.tenant_logo_url ? (
                <img src={welcomeFlash.tenant_logo_url} alt=""
                      className="max-h-16 max-w-16 object-contain" />
              ) : (
                <span className="text-[32px] font-heading text-[#0D1B3D] tracking-tight">
                  {(welcomeFlash.tenant_name?.[0] || "•").toUpperCase()}
                </span>
              )}
            </div>
            <div className="mt-4 text-[10px] tracking-[0.18em] uppercase text-[#4A5470]">Welcome to</div>
            <div className="mt-1 text-[24px] font-heading font-semibold text-[#0D1B3D] tracking-tight">
              {welcomeFlash.tenant_name}
            </div>
            <div className="mt-2 text-[12px] text-[#00668A]">
              Technology Operations Workspace
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input-auth {
          width: 100%; height: 46px; padding: 0 14px; border-radius: 10px;
          border: 1px solid #E1E7F0; background: #FAFCFF; color: #0D1B3D;
          font-size: 14px; line-height: 1.4; letter-spacing: -0.005em;
          transition: border-color 150ms ease, box-shadow 150ms ease, background 150ms ease;
        }
        .input-auth::placeholder { color: #8B95AB; }
        .input-auth:hover  { border-color: #C6D2EA; }
        .input-auth:focus  { outline: none; border-color: #2962FF; box-shadow: 0 0 0 4px rgba(41,98,255,0.14); background: #ffffff; }
        @keyframes aurora-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @keyframes aurora-drift-1 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50%      { transform: translate3d(6%, 4%, 0) scale(1.08); }
        }
        @keyframes aurora-drift-2 {
          0%, 100% { transform: translate3d(-50%, 0, 0) scale(1); }
          50%      { transform: translate3d(-46%, 3%, 0) scale(1.05); }
        }
        @keyframes aurora-drift-3 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50%      { transform: translate3d(-5%, -3%, 0) scale(1.06); }
        }
        @keyframes aurora-drift-4 {
          0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
          50%      { transform: translate3d(4%, -5%, 0) scale(1.10); }
        }
        @keyframes welcome-in {
          0%   { opacity: 0; transform: scale(0.9); }
          20%  { opacity: 1; transform: scale(1); }
          80%  { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.96); }
        }
        @keyframes tenant-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .input-auth, button, .aurora-blob { transition: none !important; animation: none !important; }
        }
      `}</style>
    </div>
  );
}

/* ------------------------------------------------------------ helpers */
function isPlausibleEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim()); }

function FormField({ label, htmlFor, trailing, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-medium tracking-[0.06em] uppercase text-[#5A5C60]">{label}</span>
        {trailing}
      </div>
      {children}
    </label>
  );
}

function ErrorAlert({ children }) {
  return (
    <div
      data-testid={AUTH.errorMsg}
      role="alert"
      aria-live="assertive"
      className="text-[13px] text-[#7A1F2B] bg-[#FBF3F1] border border-[#F0DBD6] rounded-md px-3.5 py-2.5 leading-relaxed">
      {children}
    </div>
  );
}

function PrimaryButton({ loading, children, ...rest }) {
  return (
    <button
      {...rest}
      disabled={loading || rest.disabled}
      className="w-full h-12 rounded-lg bg-[#2962FF] text-white text-[14px] font-semibold tracking-[-0.005em] hover:bg-[#1E4FDB] focus:outline-none focus:ring-4 focus:ring-[#2962FF]/25 active:bg-[#1943C0] disabled:opacity-70 flex items-center justify-center gap-2 transition-colors shadow-[0_8px_20px_-8px_rgba(41,98,255,0.55)]">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}

/* ------------------------------------------------------------ Help slide-over */
function HelpPanel({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="login-help-panel">
      <div className="absolute inset-0 bg-[#0A1A2F]/30" onClick={onClose} aria-hidden />
      <div className="ml-auto relative bg-white w-full max-w-sm h-full shadow-[-24px_0_48px_-24px_rgba(10,26,47,0.15)] p-8 flex flex-col" role="dialog" aria-label="Sign-in help">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A8B8D] font-medium">Help</div>
          <button onClick={onClose} className="text-[12px] text-[#5A5C60] hover:text-[#0A1A2F]" data-testid="login-help-close">Close</button>
        </div>
        <h3 className="mt-3 text-[20px] font-heading text-[#0A1A2F] tracking-[-0.01em]">Need help signing in?</h3>
        <ul className="mt-6 space-y-4 text-[13.5px] text-[#3B3D40] leading-relaxed">
          <li><span className="font-medium text-[#0A1A2F]">Forgot password.</span> Use the reset link on the sign-in screen — we&apos;ll email a secure one-time link that expires in 30 minutes.</li>
          <li><span className="font-medium text-[#0A1A2F]">Enterprise SSO.</span> Your organisation may use Microsoft Entra ID, SAML or OpenID Connect. Your administrator can configure it.</li>
          <li><span className="font-medium text-[#0A1A2F]">Multi-factor.</span> If MFA is required for your organisation, you&apos;ll be prompted after entering your password.</li>
          <li><span className="font-medium text-[#0A1A2F]">Passkey.</span> Available where configured — look for &ldquo;Use a passkey&rdquo; on the password screen.</li>
        </ul>
        <div className="mt-auto pt-6 border-t border-[#E7E7E4] text-[12px] text-[#5A5C60] space-y-2">
          <a href="mailto:support@suntronix.one" className="block hover:text-[#0A1A2F]">Contact support · support@suntronix.one</a>
          <a href="/help/status" className="block hover:text-[#0A1A2F]">View system status</a>
        </div>
      </div>
    </div>
  );
}
